import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Slider } from '@atlas/components';
import { Logger, SiteConfigManager, useSettings } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import {
  ChipsItem,
  Icon,
  isAvailableMission,
  Label,
  SmarticoMissionCard,
  SmarticoMissionSkeleton,
} from '@atlas/ui';

import { LOBBY_SMARTICO_BREAKPOINTS } from 'components/Casino/CasinoLobby/constants';
import { renderMissionActionButton } from 'components/Smartico/smartico-helpers';
import SmarticoMissionDetailsPopup from 'components/Smartico/SmarticoMissionDetailsPopup';
import { useIsRtl } from 'hooks/useIsRtl';
import { BASE_PATH } from 'src/common/constants';
import { CDN_TYPE_CMS, imageUrlCDN } from 'src/common/utils';
import {
  getSmarticoMissions,
  requestMissionClaimReward,
  startMission,
  useSmartico,
} from 'src/components/ClientProfile/Achievements/Smartico';

const LobbySmarticoMissions = ({
  blockTitleKey,
  imageUrl,
  maxMissionsCount,
  smarticoMissionsCategory,
}) => {
  const missionsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SMARTICO_MISSIONS);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [currentDetailsMission, setCurrentDetailsMission] = useState(null);
  const [swiper, setSwiper] = useState(null);
  const prevSlide = () => swiper && swiper.slidePrev();
  const nextSlide = () => swiper && swiper.slideNext();
  const { push: navigate } = useHistory();
  const { lang } = useSettings();
  const [isShowImg, toggleVisibilityImg] = useState(true);
  const handleError = () => toggleVisibilityImg(false);
  const [missions, setMissions] = useState([]);
  const [allMissionsCount, setAllMissionsCount] = useState(0);

  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? 'rtl' : 'ltr'), [isRtl]);

  const { isReadySmartico } = useSmartico();

  const missionsUpdates = useCallback(
    loadedMissions => {
      Logger.debug('Smartico.missions', loadedMissions);
      setAllMissionsCount(loadedMissions?.length || 0);
      const missionsFilter = smarticoMissionsCategory
        ? mission =>
            mission.ribbon?.toLowerCase() === smarticoMissionsCategory.toLowerCase() &&
            isAvailableMission(mission)
        : isAvailableMission;
      const availableMissions = (loadedMissions?.filter(missionsFilter) || []).sort((a, b) => {
        const aOrder = Number.isInteger(a.position) ? a.position : 9999;
        const bOrder = Number.isInteger(b.position) ? b.position : 9999;

        return aOrder - bOrder;
      });
      setMissions(
        maxMissionsCount ? availableMissions.splice(0, maxMissionsCount) : availableMissions,
      );
      setLoading(false);
    },
    [maxMissionsCount, smarticoMissionsCategory],
  );

  const loadMissions = useCallback(async () => {
    await getSmarticoMissions({
      cb: missionsUpdates,
      missionsUpdates,
    });
  }, [missionsUpdates]);

  const onClaimMissionReward = useCallback(
    async mission => {
      setLoading(true);

      try {
        await requestMissionClaimReward({
          achCompletedId: mission.ach_completed_id,
          missionId: mission.id,
        });
      } catch (e) {
        console.error(e);
      } finally {
        await loadMissions();
        setLoading(false);
      }
    },
    [loadMissions],
  );

  const onStartMission = useCallback(
    async missionId => {
      setLoading(true);

      try {
        await startMission({
          missionId,
        });
      } catch (e) {
        console.error(e);
      } finally {
        await loadMissions();
        setLoading(false);
      }
    },
    [loadMissions],
  );

  const onOpenMissionDetails = useCallback(mission => {
    setCurrentDetailsMission(mission);
    setShowDetailsPopup(true);
  }, []);

  const onCloseMissionDetails = useCallback(() => {
    setShowDetailsPopup(false);
    setCurrentDetailsMission(null);
  }, []);

  useEffect(() => {
    if (isReadySmartico) {
      setMissions([]);
      setLoading(true);
      setTimeout(() => {
        loadMissions();
      }, 1000);
    }
  }, [isReadySmartico, loadMissions, lang]);

  if (!loading && missions.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-smartico-missions">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={32}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/missions.png`
                }
                width={32}
              />
            </div>
          )}

          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
        <div className="casino-lobby__right">
          <ChipsItem
            className="casino__category__view-all"
            label={
              <div className="casino__category__title__view">
                <Label className="casino__category__title__text" message="View_All" />
                <span>({loading ? '...' : allMissionsCount})</span>
              </div>
            }
            onClick={() => navigate(missionsPath)}
          />

          {!isLocked && (
            <>
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-left"
                label={<Icon name="arrow-left" />}
                onClick={prevSlide}
              />
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-right"
                label={<Icon name="arrow-right" />}
                onClick={nextSlide}
              />
            </>
          )}
        </div>
      </div>
      <div className="casino-lobby__content">
        <div className="lobby-smartico-missions__slider">
          <Slider
            autoplay={false}
            breakpoints={LOBBY_SMARTICO_BREAKPOINTS}
            breakpointsBase="container"
            dir={dir}
            direction="horizontal"
            loop={false}
            onBeforeInit={setSwiper}
            onLock={() => setIsLocked(true)}
            onUnlock={() => setIsLocked(false)}
            onUpdate={swiperInstance => setIsLocked(swiperInstance.isLocked)}
            slidesPerView={4}
            spaceBetween={8}
            speed={1000}
          >
            {!loading
              ? missions.map(mission => (
                  <Slider.Slide key={mission.id}>
                    <SmarticoMissionCard
                      mission={mission}
                      onClick={() => onOpenMissionDetails(mission)}
                      renderActionButton={mission =>
                        renderMissionActionButton(mission, {
                          onClaimMissionReward,
                          onOpenMissionDetails,
                          onStartMission,
                        })
                      }
                    />
                  </Slider.Slide>
                ))
              : Array.from({ length: 10 }).map((_, index) => (
                  <Slider.Slide key={`skeleton-${index}`}>
                    <SmarticoMissionSkeleton />
                  </Slider.Slide>
                ))}
          </Slider>
        </div>
      </div>

      {showDetailsPopup && (
        <SmarticoMissionDetailsPopup
          mission={currentDetailsMission}
          onClaimMissionReward={onClaimMissionReward}
          onClose={onCloseMissionDetails}
          onStartMission={onStartMission}
        />
      )}
    </div>
  );
};

export default LobbySmarticoMissions;
